import { useEffect, useRef, useState } from "react";

type Position = {
  top: number;
  left: number;
};

type UseAnchoredFixedMenuPositionParams = {
  isOpen: boolean;
  menuWidth?: number;
  verticalOffset?: number;
  viewportPadding?: number;
};

/*
 * computes and maintains a fixed dropdown position anchored to a trigger button
 * while keeping the menu fully visible within the viewport.
 */
const useAnchoredFixedMenuPosition = ({
  isOpen,
  menuWidth = 340,
  verticalOffset = 6,
  viewportPadding = 8,
}: UseAnchoredFixedMenuPositionParams) => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [position, setPosition] = useState<Position | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const updatePosition = () => {
      if (!anchorRef.current) {
        return;
      }

      const buttonRect = anchorRef.current.getBoundingClientRect();
      const left = Math.min(
        Math.max(viewportPadding, buttonRect.right - menuWidth),
        window.innerWidth - menuWidth - viewportPadding,
      );

      setPosition({
        top: buttonRect.bottom + verticalOffset,
        left,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, menuWidth, verticalOffset, viewportPadding]);

  return {
    anchorRef,
    position,
  };
};

export default useAnchoredFixedMenuPosition;
